import * as React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { cn } from '../lib/cn';
import {
  applyTheme,
  getThemePreference,
  setThemePreference,
  subscribeToSystemTheme,
  type ThemePreference,
} from '../lib/theme';

/**
 * ThemeToggle — light / dark / follow-the-system, as a segmented control.
 *
 * WHY THREE BUTTONS AND NOT ONE THAT CYCLES
 * ─────────────────────────────────────────
 * A cycling button is smaller, and it makes the user guess: from `dark`, does
 * one press give light or system? They cannot know without pressing, and a
 * wrong press repaints the whole page. Three targets means every state is one
 * predictable click away, and the current one is visible without opening
 * anything. The cost is ~90px of nav, which the right cluster has.
 *
 * It is deliberately NOT inside the UserMenu. A theme control that requires
 * opening a dropdown to discover is a theme control most people never find,
 * and this one has to be findable enough that nobody edits index.html to get
 * a dark screen.
 *
 * ACCESSIBILITY
 * ─────────────
 * `radiogroup` semantics, because this is one setting with three mutually
 * exclusive values — not three independent buttons, which is what `aria-pressed`
 * would announce. Each option carries a real label; the icons are decorative
 * and marked `aria-hidden`. The active option is conveyed by `aria-checked`,
 * not by colour alone.
 *
 * STATE
 * ─────
 * The DOM is the source of truth for what is PAINTED (`<html data-theme>`, set
 * before first paint by the inline script). This component's state tracks the
 * stored PREFERENCE, which is a different thing: `system` is a preference that
 * has no fixed painted value.
 */

const OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  label: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { value: 'light', label: 'Light', hint: 'Always use the light theme', icon: Sun },
  { value: 'dark', label: 'Dark', hint: 'Always use the dark theme', icon: Moon },
  { value: 'system', label: 'System', hint: "Follow this device's setting", icon: Monitor },
];

export interface ThemeToggleProps {
  /** Render on a dark, opaque nav ground (the portal top bar) rather than on
   *  a themed page surface. The top bar paints its own accent colour in BOTH
   *  themes, so its controls cannot use `foreground`/`card` — those flip. */
  onAccent?: boolean;
  className?: string;
}

export function ThemeToggle({ onAccent = false, className }: ThemeToggleProps) {
  // `system` on the server / first render, then corrected in the effect below.
  // Reading localStorage during render would make this component impossible to
  // server-render and would tear if two instances mounted.
  const [preference, setPreference] = React.useState<ThemePreference>('system');

  React.useEffect(() => {
    setPreference(getThemePreference());
    // Re-apply on mount so a preference written by another tab still lands.
    applyTheme(getThemePreference());
    return subscribeToSystemTheme(() => {
      // Only fires while the preference is `system`; the painted value changed
      // but the preference did not, so nothing to set here beyond a re-render.
      setPreference('system');
    });
  }, []);

  function choose(next: ThemePreference) {
    setPreference(next);
    setThemePreference(next);
  }

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn(
        'inline-flex items-center gap-0.5 rounded p-0.5',
        onAccent ? 'bg-black/20' : 'bg-muted',
        className,
      )}
    >
      {OPTIONS.map(({ value, label, hint, icon: Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={hint}
            onClick={() => choose(value)}
            className={cn(
              'inline-flex size-7 items-center justify-center rounded-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
              onAccent
                ? [
                    'focus-visible:ring-offset-transparent',
                    // Navy on the primary, never white — 5.09:1 vs 2.26:1
                    // (CLAUDE.md §4.2). The same lock as every other control
                    // that sits on the brand orange.
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-white/70 hover:bg-white/10 hover:text-white',
                  ]
                : [
                    'focus-visible:ring-offset-card',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-card-hover hover:text-foreground',
                  ],
            )}
          >
            <Icon className="size-4" aria-hidden />
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
